import type { FramingOptionValue } from "~/utils/demandFraming"
import { formatName } from "~/utils/namingUtils"

/**
 * The weekly curve a variant's level is spread over. Every curve but `lastYear`, `ownYear` and
 * `standard` is the DS-ingested `SeasonalityProfiles` row of the variant's category; `lastYear` is
 * its own sales; `ownYear` is a New variant's own sales plus the estimate for the weeks before it
 * sold; `standard` needs no input, so a variant always has one.
 */
export const SEASONALITY_PROFILE_IDS = [
  "season",
  "calendar",
  "intro",
  "mix",
  "lastYear",
  "ownYear",
  "standard",
] as const

export type SeasonalityProfileId = (typeof SEASONALITY_PROFILE_IDS)[number]

/** The curves whose input is a curve; `ownYear` is available by its own-sales timing instead. */
export type CurveProfileId = Exclude<SeasonalityProfileId, "ownYear">

type SeasonalityProfileOption = { label: string; description: string; value?: FramingOptionValue }

/** New variant's launch-curve share with no stored share and no model weight: an even blend. */
export const DEFAULT_LAUNCH_SHARE = 0.5

/** The one copy of each curve's wording: the framing question, the picker and the badges read it. */
/* eslint-disable @hakio/no-untranslated-terminology -- static option copy, as in `FRAMING_QUESTIONS` */
export const SEASONALITY_PROFILE_OPTIONS = {
  season: {
    label: "Season curve",
    description: "How past seasons sold, week by week from the season start.",
  },
  calendar: {
    label: "Category seasonality",
    description: "The category's sales pattern over the year, week by week.",
  },
  intro: {
    label: "Launch curve",
    description: "How the category's new products sell in the weeks after launch.",
  },
  mix: {
    label: "New variant",
    description:
      "How the category's new products sell after launch, blended with category seasonality.",
    value: {
      control: "slider",
      label: "Launch curve share",
      default: DEFAULT_LAUNCH_SHARE * 100,
      decimals: 0,
      describe: (pct: number) => `${pct}% launch curve, ${100 - pct}% category seasonality`,
      summary: (pct: number) => `${pct}% launch curve`,
    },
  },
  lastYear: {
    label: "Last year",
    description: "This variant's own sales last year, week by week.",
  },
  ownYear: {
    label: "Last year + estimate",
    description:
      "Its own sales a year earlier, and the estimated unobserved demand for the weeks before it sold.",
  },
  standard: {
    label: "Standard",
    description: "A simple standard pattern, for when no other is available.",
  },
} satisfies Record<SeasonalityProfileId, SeasonalityProfileOption>
/* eslint-enable @hakio/no-untranslated-terminology */

/** The framing question's options, in display order. Unanswered follows the recommendation. */
export const SEASONALITY_PROFILE_FRAMING_OPTIONS = SEASONALITY_PROFILE_IDS.map((id) => ({
  id,
  ...SEASONALITY_PROFILE_OPTIONS[id],
}))

const seasonalityProfileLabel = (id: SeasonalityProfileId): string =>
  SEASONALITY_PROFILE_OPTIONS[id].label

export const isSeasonalityProfileId = (id: string): id is SeasonalityProfileId =>
  (SEASONALITY_PROFILE_IDS as readonly string[]).includes(id)

/**
 * Which curves a variant can take, by how it is planned:
 * - `fashionNew` / `fashionCarryOver`: a dated season, curve by weeks since the season start.
 * - `noosNew`: a NOOS variant planned on its matches (the intro × calendar blend).
 * - `noosIntroduced`: a NOOS variant in its first year, planned on its own sales; its category's
 *   launch curve can still shape its first year.
 * - `noosMature`: a NOOS variant past its first year, on its own sales.
 */
export type SeasonalityProfileGroup =
  | "fashionNew"
  | "fashionCarryOver"
  | "noosNew"
  | "noosIntroduced"
  | "noosMature"

/**
 * Preference order, `standard` excluded: the first available is recommended, and applied while no
 * answer is stored. A variant's own year, when it has enough selling weeks to back it, comes first.
 */
const PREFERRED: Record<SeasonalityProfileGroup, readonly SeasonalityProfileId[]> = {
  fashionNew: ["season"],
  fashionCarryOver: ["lastYear", "season"],
  noosNew: ["mix", "calendar", "intro"],
  noosIntroduced: ["ownYear", "calendar", "mix", "intro"],
  noosMature: ["lastYear", "calendar"],
}

export type SeasonalityProfileChoice = {
  id: SeasonalityProfileId
  /** `mix` only: the planner's intro share in %, null for the model's weight. */
  introPct: number | null
}

/**
 * A New NOOS variant on `ownYear`: the plan weeks its sales a year earlier time, and what times the
 * weeks before it sold. `estimate` is the estimated unobserved demand's own weeks; `profile` is
 * Category seasonality, where the estimate has none.
 */
export type OwnSalesTiming = {
  ownWeeks: number
  fillWeeks: number
  fillBy: "estimate" | "profile"
}

export type SeasonalityProfileState = {
  group: SeasonalityProfileGroup
  /** The curve the engine spread the level over. Never `intro` or `calendar` where `mix` is offered. */
  applied: SeasonalityProfileId
  /** `mix` and `intro`: the launch curve's share it used, 0–1. */
  introWeight: number | null
  /** What `mix` starts at unanswered: the recommendation's share where it is one of `mix`'s ends, else the model's. */
  defaultIntroWeight: number
  recommended: SeasonalityProfileId
  /** Offered, in display order; `standard` always last. */
  available: SeasonalityProfileId[]
  /** A stored answer chose `applied`, rather than the recommendation. */
  chosen: boolean
  /** `ownYear` only: what times which of its plan weeks. */
  ownSales: OwnSalesTiming | null
}

/**
 * Where `mix` is offered, the launch curve and Category seasonality alone are its slider's ends, so
 * they resolve, are offered and are badged as `mix` at that share.
 */
const MIX_END_SHARE: Partial<Record<SeasonalityProfileId, number>> = { intro: 1, calendar: 0 }

/**
 * The applied curve: the stored choice when the group offers it and its input exists, else the
 * first available preferred curve, else `standard`. `standard` is never recommended while another
 * curve is available. `ownYear` is available exactly when `ownSales` is given.
 */
export const resolveSeasonalityProfile = (
  group: SeasonalityProfileGroup,
  has: (id: CurveProfileId) => boolean,
  choice: SeasonalityProfileChoice | null,
  modelIntroWeight: number,
  ownSales: OwnSalesTiming | null,
): SeasonalityProfileState => {
  const offered = [
    ...PREFERRED[group].filter((id) => (id === "ownYear" ? ownSales != null : has(id))),
    "standard" as const,
  ]
  const canMix = offered.includes("mix")
  const endShare = (id: SeasonalityProfileId) => (canMix ? MIX_END_SHARE[id] : undefined)
  const folded = (id: SeasonalityProfileId) => (endShare(id) == null ? id : "mix")
  const chosen = choice != null && offered.includes(choice.id)
  const pick = chosen ? choice : { id: offered[0]!, introPct: null }
  const applied = folded(pick.id)
  return {
    group,
    applied,
    introWeight: introWeightOf(
      applied,
      endShare(pick.id) ?? pctShare(pick.introPct),
      modelIntroWeight,
    ),
    defaultIntroWeight: endShare(offered[0]!) ?? modelIntroWeight,
    recommended: folded(offered[0]!),
    available: offered.filter((id) => endShare(id) == null),
    chosen,
    ownSales: applied === "ownYear" ? ownSales : null,
  }
}

const pctShare = (pct: number | null) => (pct == null ? null : pct / 100)

const introWeightOf = (
  applied: SeasonalityProfileId,
  share: number | null,
  modelIntroWeight: number,
): number | null => {
  if (applied === "intro") return 1
  if (applied === "mix") return share ?? modelIntroWeight
  return null
}

export const SEASONALITY_PROFILE_CLARIFIER =
  "This only sets the weekly shape: when demand lands, not how much."

export type SeasonalityProfileBadge = {
  label: string
  variant: "violet" | "gray"
  /** Hakio's AI launch curve shapes the weeks. */
  isAi: boolean
  description: string
}

export type SeasonalityProfileBadgeInput = Pick<
  SeasonalityProfileState,
  "applied" | "chosen" | "ownSales" | "introWeight"
>

const weeksText = (n: number) => `${n} week${n === 1 ? "" : "s"}`

/** What times a New variant's plan on `ownYear`: its own last year, then the estimate. */
export const ownSalesTimingText = (timing: OwnSalesTiming): string => {
  const own = `${weeksText(timing.ownWeeks)} follow its own ${formatName("sale", true)} a year earlier.`
  const fill =
    timing.fillBy === "estimate"
      ? "The estimated unobserved demand"
      : seasonalityProfileLabel("calendar")
  return `${own} ${fill} times the ${weeksText(timing.fillWeeks)} before it sold.`
}

/** A curve's name for this variant: `ownYear` names Category seasonality where the estimate has no weeks. */
export const seasonalityProfileOptionLabel = (
  id: SeasonalityProfileId,
  ownSales: OwnSalesTiming | null,
): string => {
  if (id === "ownYear" && ownSales?.fillBy === "profile")
    return `Last year + ${seasonalityProfileLabel("calendar")}`
  return seasonalityProfileLabel(id)
}

/** The When card's and the Total grid's badge: violet for a planner's pick or an AI curve. */
export const seasonalityProfileBadge = (
  profile: SeasonalityProfileBadgeInput | null,
): SeasonalityProfileBadge | null => {
  if (!profile) return null
  const isAi = (profile.introWeight ?? 0) > 0
  return {
    label: seasonalityProfileOptionLabel(profile.applied, profile.ownSales),
    variant: profile.chosen || isAi ? "violet" : "gray",
    isAi,
    description: badgeDescription(profile),
  }
}

const badgeDescription = ({ applied, ownSales, introWeight }: SeasonalityProfileBadgeInput) => {
  if (ownSales) return ownSalesTimingText(ownSales)
  if (applied === "mix" && introWeight != null)
    return SEASONALITY_PROFILE_OPTIONS.mix.value.describe(Math.round(introWeight * 100))
  return SEASONALITY_PROFILE_OPTIONS[applied].description
}

const WEEKS = 52

/** NOOS `standard`: every week of the year alike. */
export const STANDARD_CALENDAR: ReadonlyMap<number, number> = new Map(
  Array.from({ length: WEEKS }, (_, i) => [i + 1, 1 / WEEKS]),
)

/**
 * Fashion `standard`, by weeks since the season start: falling linearly from the first week to the
 * 52nd. DS's own fallback when a company has too few finished seasons (`CollectionProfiles`).
 */
export const STANDARD_SEASON: readonly number[] = (() => {
  const total = (WEEKS * (WEEKS + 1)) / 2
  return Array.from({ length: WEEKS }, (_, i) => (WEEKS - i) / total)
})()

/** Below this many selling weeks a variant's own year is too thin to time demand by. */
export const MIN_LAST_YEAR_SELLING_WEEKS = 10

export const sellingWeeks = (weekly: readonly number[]): number =>
  weekly.filter((q) => q > 0).length

/**
 * Enough selling weeks to time demand by. NOOS counts only the plan's weeks (`planWeeks`), as the
 * When card does; fashion counts the whole earlier season, its curve being by weeks since start.
 */
export const lastYearIsReliable = (weekly: readonly number[], planWeeks?: number): boolean =>
  sellingWeeks(planWeeks == null ? weekly : weekly.slice(0, planWeeks)) >=
  MIN_LAST_YEAR_SELLING_WEEKS

/** A variant's own weekly sales as a curve, net-return weeks at zero; null when it never sold. */
export const lastYearCurve = (weekly: readonly number[] | undefined): number[] | null => {
  if (!weekly) return null
  const clipped = weekly.map((q) => Math.max(0, q))
  return sellingWeeks(clipped) > 0 ? clipped : null
}

/**
 * NOOS Last year is offered in the viewed scope: the scope's own last year (the When card's
 * `referenceLy`, over the plan's weeks) has enough selling weeks, or it is the applied curve
 * already. The profile is one per variant, so the scope never changes which curve is spread by.
 * Fashion counts its earlier season, which no scope narrows.
 */
export const lastYearOfferedInScope = (
  state: SeasonalityProfileState,
  scopedLastYear: readonly number[],
): boolean =>
  state.group !== "noosMature" ||
  state.applied === "lastYear" ||
  sellingWeeks(scopedLastYear) >= MIN_LAST_YEAR_SELLING_WEEKS
